/**
 * EditableTable — a generic, premium, editable data-grid rendered as a real
 * ``<table>`` (Linear / Glide-grade), NOT markdown.
 *
 * The star of the redesigned AI-proposal review surface: it renders any
 * record-array — a work-cycle, a BOM, invoice lines — as inline-editable
 * rows with drag-reorder, click-to-source, low-confidence signalling and a
 * calm empty state. Purely presentational + fully controlled (no fetching,
 * no store, nothing domain-specific).
 *
 * Pencil aesthetic + DRY reuse (single source of truth = ``DataPagedTable``):
 *   - Table chrome — cream root + hairline border, ``bg-cream-deep`` sticky
 *     header, uppercase ``text-[10px]`` tracking th, ``tabular-nums`` cells —
 *     mirrors ``DataPagedTable.tsx`` (root L128, thead L139, th L146, td L178).
 *   - Per-cell display routes through ``formatCellValue`` (DataPagedTable
 *     L57) so null→"—", number→it-IT locale, string→passthrough stay
 *     identical across every structured-data surface.
 *   - Low-confidence dot mirrors the ``ActionFieldEditor`` confidence marker
 *     (L1381-1391: ``h-1.5 w-1.5 rounded-full`` + ``bg-warning``) — SOTA
 *     restraint: shown ONLY for ``"low"``, quiet on high-confidence rows.
 *   - Provenance chip mirrors the ``ActionFieldEditor`` citation marker
 *     (L1373-1380: ``bg-ando-orange-soft`` + ``text-accent``), rendered mono.
 *   - Flag pill borrows the amber band from ``SqlConfidenceBadge`` (media).
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { GripVertical, Plus, X } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { formatCellValue } from "./DataPagedTable";

export interface EditableTableColumn {
  key: string;
  label: string;
  /** Text alignment. Default ``"left"``; use ``"right"`` for numeric columns. */
  align?: "left" | "right";
  /** CSS width, e.g. ``"78px"``. */
  width?: string;
  /** Whether cells in this column are click-to-edit. Default ``false``. */
  editable?: boolean;
  /** Coercion + input hint. Default ``"text"``. */
  type?: "text" | "number";
  /** Render values in the monospace face (codes / SKUs). */
  mono?: boolean;
}

export interface EditableTableRow {
  id: string;
  cells: Record<string, string | number | null>;
  /** A dot is shown ONLY for ``"low"`` — stay quiet on high-confidence rows. */
  confidence?: "high" | "low";
  /** Small amber label next to the first editable cell, e.g. "tempo anomalo". */
  flag?: string;
  /** Clickable mono chip → ``onRowSource``. */
  provenance?: { label: string; sourceKey?: string };
}

export interface EditableTableProps {
  columns: EditableTableColumn[];
  rows: EditableTableRow[];
  onCellChange?: (rowId: string, key: string, value: string | number) => void;
  onAddRow?: () => void;
  /** Default localized "Aggiungi riga". */
  addRowLabel?: string;
  onRemoveRow?: (rowId: string) => void;
  onReorder?: (fromId: string, toId: string) => void;
  /** Click-to-source — row click (off editable/chip) or provenance chip. */
  onRowSource?: (row: EditableTableRow) => void;
  /** Externally-highlighted row (driven by click-to-source elsewhere). */
  activeRowId?: string;
  /** ``compact`` = 34px rows (default), ``comfortable`` = 42px rows. */
  density?: "compact" | "comfortable";
  /** Rendered in a styled footer bar (e.g. "5 fasi · 355 min"). */
  footer?: React.ReactNode;
  className?: string;
  // ── Collaboration (banco field-locks) — all OPTIONAL, default off so every
  // existing call site is untouched. ─────────────────────────────────────────
  /** Map a cell to the flat field-path the host locks on. Omit → no locking. */
  lockKeyFor?: (rowId: string, key: string) => string;
  /** Cells locked by SOMEONE ELSE (host filters out self), keyed by that path.
   * A present entry disables inline edit and shows a "in modifica da …" badge. */
  lockedCells?: Record<string, CellLock>;
  /** Inline edit started on a cell — host acquires the field-lock. */
  onCellFocus?: (rowId: string, key: string) => void;
  /** Inline edit ended (commit/cancel/blur) — host releases the field-lock. */
  onCellBlur?: (rowId: string, key: string) => void;
}

/** Who currently holds a cell (display only). */
export interface CellLock {
  readonly name: string;
  readonly color: string;
  readonly initials?: string;
}

interface EditState {
  readonly rowId: string;
  readonly key: string;
}

/**
 * Coerce the raw ``<input>`` string to the column's type. ``number`` columns
 * yield a JS ``number`` when the trimmed text is finite; empty / non-numeric
 * text falls back to the raw string so the caller never loses user input.
 */
function coerceCellValue(raw: string, type: EditableTableColumn["type"]): string | number {
  if (type === "number") {
    const trimmed = raw.trim();
    if (trimmed === "") return raw;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : raw;
  }
  return raw;
}

/** Read a cell value defensively (missing key → null). */
function readCell(row: EditableTableRow, key: string): string | number | null {
  const value = row.cells[key];
  return value === undefined ? null : value;
}

export function EditableTable({
  columns,
  rows,
  onCellChange,
  onAddRow,
  addRowLabel,
  onRemoveRow,
  onReorder,
  onRowSource,
  activeRowId,
  density = "compact",
  footer,
  className,
  lockKeyFor,
  lockedCells,
  onCellFocus,
  onCellBlur,
}: EditableTableProps): React.JSX.Element {
  const { i18n } = useLingui();

  const [editing, setEditing] = React.useState<EditState | null>(null);
  const [draft, setDraft] = React.useState<string>("");
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [dragOverId, setDragOverId] = React.useState<string | null>(null);

  const canReorder = Boolean(onReorder);
  const canSource = Boolean(onRowSource);
  const hasProvenance = rows.some((row) => row.provenance !== undefined);
  const hasLowConfidence = rows.some((row) => row.confidence === "low");
  // Leading gutter carries the drag handle + the low-confidence status dot.
  const showLeading = canReorder || hasLowConfidence;
  // Trailing gutter carries the provenance chip + the hover remove control.
  const showTrailing = Boolean(onRemoveRow) || hasProvenance;
  const firstEditableKey = columns.find((col) => col.editable)?.key;

  const rowHeight = density === "comfortable" ? "h-[42px]" : "h-[34px]";
  const columnSpan = columns.length + (showLeading ? 1 : 0) + (showTrailing ? 1 : 0);
  const resolvedAddRowLabel = addRowLabel ?? i18n._(msg`Aggiungi riga`);

  const beginEdit = React.useCallback(
    (row: EditableTableRow, key: string): void => {
      const current = readCell(row, key);
      setDraft(current === null ? "" : String(current));
      setEditing({ rowId: row.id, key });
      onCellFocus?.(row.id, key); // acquire the field-lock
    },
    [onCellFocus],
  );

  const cancelEdit = React.useCallback((): void => {
    setEditing((prev) => {
      if (prev) onCellBlur?.(prev.rowId, prev.key);
      return null;
    });
  }, [onCellBlur]);

  const commitEdit = React.useCallback((): void => {
    if (editing === null) return;
    const { rowId, key } = editing;
    const row = rows.find((candidate) => candidate.id === rowId);
    const column = columns.find((candidate) => candidate.key === key);
    if (row !== undefined) {
      const original = readCell(row, key);
      const originalText = original === null ? "" : String(original);
      if (draft !== originalText) {
        onCellChange?.(rowId, key, coerceCellValue(draft, column?.type));
      }
    }
    onCellBlur?.(rowId, key); // release the field-lock
    setEditing(null);
  }, [editing, draft, rows, columns, onCellChange, onCellBlur]);

  const handleReorderKey = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, rowId: string): void => {
      if (onReorder === undefined) return;
      const index = rows.findIndex((row) => row.id === rowId);
      if (index === -1) return;
      if (event.key === "ArrowUp" && index > 0) {
        event.preventDefault();
        onReorder(rowId, rows[index - 1]!.id);
      } else if (event.key === "ArrowDown" && index < rows.length - 1) {
        event.preventDefault();
        onReorder(rowId, rows[index + 1]!.id);
      }
    },
    [onReorder, rows],
  );

  const alignClass = (column: EditableTableColumn): string =>
    column.align === "right" ? "text-right" : "text-left";
  const numericClass = (column: EditableTableColumn): string =>
    column.type === "number" || column.align === "right" ? "tabular-nums" : "";

  return (
    <div className={cn("flex flex-col rounded-md border border-hairline bg-cream", className)}>
      <div className="min-w-0 overflow-x-auto">
        <table className="w-full border-collapse text-[11px]">
          <thead className="sticky top-0 z-10 bg-cream-deep/80 backdrop-blur-sm">
            <tr className="border-hairline border-b">
              {showLeading ? (
                <th scope="col" className="w-9 px-1.5 py-1.5">
                  <span className="sr-only">{i18n._(msg`Stato riga`)}</span>
                </th>
              ) : null}
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    "whitespace-nowrap px-2 py-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.04em]",
                    alignClass(column),
                  )}
                >
                  {column.label}
                </th>
              ))}
              {showTrailing ? (
                <th scope="col" className="w-px px-2 py-1.5 text-right">
                  <span className="sr-only">{i18n._(msg`Azioni riga`)}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columnSpan} className="px-3 py-8 text-center text-[11px] text-ink-3">
                  {i18n._(msg`Nessuna riga`)}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isActive = row.id === activeRowId;
                const isDragOver = row.id === dragOverId && row.id !== dragId;
                const rowClickable = canSource;
                return (
                  <tr
                    key={row.id}
                    data-active={isActive ? "true" : undefined}
                    // A clickable row is a redundant pointer affordance for
                    // click-to-source; keyboard users reach the same action by
                    // focusing the row (tabIndex) + Enter/Space, or via the
                    // focusable provenance chip. Guard target===currentTarget so
                    // Enter inside an editable input never triggers a source jump.
                    tabIndex={rowClickable ? 0 : undefined}
                    className={cn(
                      "group border-hairline border-b transition-colors last:border-b-0",
                      rowClickable &&
                        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ando-orange/40 focus-visible:ring-inset",
                      isActive && "bg-ando-orange/[0.06]",
                      !isActive && rowClickable && "cursor-pointer hover:bg-ando-orange/[0.04]",
                      isDragOver && "shadow-[inset_0_2px_0_0_#FF7A1A]",
                    )}
                    onClick={rowClickable ? () => onRowSource?.(row) : undefined}
                    onKeyDown={
                      rowClickable
                        ? (event) => {
                            if (
                              event.target === event.currentTarget &&
                              (event.key === "Enter" || event.key === " ")
                            ) {
                              event.preventDefault();
                              onRowSource?.(row);
                            }
                          }
                        : undefined
                    }
                    onDragOver={
                      canReorder
                        ? (event) => {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                            if (dragOverId !== row.id) setDragOverId(row.id);
                          }
                        : undefined
                    }
                    onDrop={
                      canReorder
                        ? (event) => {
                            event.preventDefault();
                            const fromId = event.dataTransfer.getData("text/plain") || dragId;
                            setDragOverId(null);
                            setDragId(null);
                            if (fromId && fromId !== row.id) {
                              onReorder?.(fromId, row.id);
                            }
                          }
                        : undefined
                    }
                  >
                    {showLeading ? (
                      <td
                        className={cn(
                          "border-l-2 border-l-transparent px-1.5 align-middle",
                          rowHeight,
                          isActive && "border-l-ando-orange",
                        )}
                      >
                        <div className="flex items-center gap-1">
                          {canReorder ? (
                            <button
                              type="button"
                              draggable
                              aria-label={i18n._(msg`Trascina per riordinare`)}
                              className="cursor-grab rounded-sm p-0.5 text-ink-4 opacity-0 transition-colors hover:text-ink-2 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ando-orange/40 active:cursor-grabbing group-hover:opacity-100 data-[active=true]:opacity-100"
                              data-active={isActive ? "true" : undefined}
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => handleReorderKey(event, row.id)}
                              onDragStart={(event) => {
                                event.dataTransfer.effectAllowed = "move";
                                event.dataTransfer.setData("text/plain", row.id);
                                setDragId(row.id);
                              }}
                              onDragEnd={() => {
                                setDragId(null);
                                setDragOverId(null);
                              }}
                            >
                              <GripVertical className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          ) : null}
                          {row.confidence === "low" ? (
                            <span
                              role="img"
                              aria-label={i18n._(msg`Affidabilità bassa`)}
                              title={i18n._(msg`Affidabilità bassa`)}
                              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-warning ring-2 ring-warning/25"
                            />
                          ) : null}
                        </div>
                      </td>
                    ) : null}

                    {columns.map((column, columnIndex) => {
                      const value = readCell(row, column.key);
                      const display = formatCellValue(value, i18n);
                      const isLeadingCol = columnIndex === 0 && !showLeading;
                      const isEditing = editing?.rowId === row.id && editing.key === column.key;
                      const showFlag = column.key === firstEditableKey && row.flag !== undefined;
                      // A cell held by a PEER (host already filtered out self):
                      // show who, block inline edit until they release it.
                      const lockPath = lockKeyFor?.(row.id, column.key);
                      const cellLock = lockPath ? lockedCells?.[lockPath] : undefined;

                      return (
                        <td
                          key={column.key}
                          style={column.width ? { width: column.width } : undefined}
                          className={cn(
                            "px-2 align-middle text-ink-2",
                            rowHeight,
                            alignClass(column),
                            numericClass(column),
                            column.mono && "font-mono",
                            isLeadingCol && "border-l-2 border-l-transparent",
                            isLeadingCol && isActive && "border-l-ando-orange",
                          )}
                        >
                          <div
                            className={cn(
                              "flex min-w-0 items-center gap-1.5",
                              column.align === "right" && "justify-end",
                            )}
                          >
                            {showFlag ? (
                              <span className="inline-flex shrink-0 items-center rounded-sm bg-amber-500/14 px-1.5 py-0.5 font-medium text-[9px] text-amber-700 uppercase tracking-[0.04em] ring-1 ring-amber-500/25">
                                {row.flag}
                              </span>
                            ) : null}
                            {cellLock ? (
                              <span
                                title={i18n._(msg`In modifica da ${cellLock.name}`)}
                                className={cn(
                                  "flex w-full min-w-0 items-center gap-1.5 rounded-sm px-1.5 py-1 text-[11px] text-ink-2 ring-1",
                                  alignClass(column),
                                  numericClass(column),
                                  column.mono && "font-mono",
                                )}
                                style={{ boxShadow: `inset 0 0 0 1px ${cellLock.color}66` }}
                              >
                                <span
                                  aria-hidden
                                  className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full text-[8px] text-white"
                                  style={{ backgroundColor: cellLock.color }}
                                >
                                  {cellLock.initials ?? "•"}
                                </span>
                                <span className="min-w-0 truncate">{display}</span>
                              </span>
                            ) : column.editable ? (
                              isEditing ? (
                                <input
                                  // biome-ignore lint/a11y/noAutofocus: focus is intentional — the cell was just activated for inline edit.
                                  autoFocus
                                  value={draft}
                                  inputMode={column.type === "number" ? "decimal" : undefined}
                                  aria-label={column.label}
                                  className={cn(
                                    "w-full min-w-0 rounded-sm border border-ando-orange/40 bg-white px-1.5 py-0.5 text-[11px] text-ink-1 outline-none ring-2 ring-ando-orange/20",
                                    alignClass(column),
                                    numericClass(column),
                                    column.mono && "font-mono",
                                  )}
                                  onChange={(event) => setDraft(event.target.value)}
                                  onClick={(event) => event.stopPropagation()}
                                  onBlur={commitEdit}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      commitEdit();
                                    } else if (event.key === "Escape") {
                                      event.preventDefault();
                                      cancelEdit();
                                    }
                                  }}
                                />
                              ) : (
                                <button
                                  type="button"
                                  aria-label={i18n._(msg`Modifica ${column.label}`)}
                                  className={cn(
                                    "w-full min-w-0 truncate rounded-sm px-1.5 py-1 text-[11px] text-ink-1 transition-colors",
                                    "hover:bg-ando-orange/[0.07] hover:ring-1 hover:ring-ando-orange/20",
                                    "focus-visible:bg-ando-orange/[0.07] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ando-orange/30",
                                    alignClass(column),
                                    numericClass(column),
                                    column.mono && "font-mono",
                                    value === null && "text-ink-4",
                                  )}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    beginEdit(row, column.key);
                                  }}
                                >
                                  {display}
                                </button>
                              )
                            ) : (
                              <span className="min-w-0 truncate" title={display}>
                                {display}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}

                    {showTrailing ? (
                      <td className={cn("px-2 align-middle", rowHeight)}>
                        <div className="flex items-center justify-end gap-1.5">
                          {row.provenance ? (
                            <button
                              type="button"
                              title={i18n._(msg`Apri fonte: ${row.provenance.label}`)}
                              data-source-key={row.provenance.sourceKey}
                              className="inline-flex h-4 max-w-[8rem] items-center truncate rounded-sm bg-ando-orange-soft px-1 font-medium font-mono text-[10px] text-accent transition-colors hover:bg-ando-orange/20"
                              onClick={(event) => {
                                event.stopPropagation();
                                onRowSource?.(row);
                              }}
                            >
                              {row.provenance.label}
                            </button>
                          ) : null}
                          {onRemoveRow ? (
                            <button
                              type="button"
                              aria-label={i18n._(msg`Rimuovi riga`)}
                              className="rounded-sm p-0.5 text-ink-4 opacity-0 transition-colors hover:text-red-600 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-red-400/40 group-hover:opacity-100"
                              onClick={(event) => {
                                event.stopPropagation();
                                onRemoveRow(row.id);
                              }}
                            >
                              <X className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {onAddRow ? (
        <button
          type="button"
          className={cn(
            "flex w-full items-center justify-center gap-1.5 border-hairline border-t border-dashed px-3 py-2",
            "font-medium text-[11px] text-ink-3 transition-colors",
            "hover:bg-ando-orange/[0.05] hover:text-accent",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ando-orange/40 focus-visible:ring-inset",
          )}
          onClick={onAddRow}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          <span>{resolvedAddRowLabel}</span>
        </button>
      ) : null}

      {footer ? (
        <div className="flex items-center justify-between gap-2 border-hairline border-t bg-cream-deep/40 px-3 py-2 text-[11px] text-ink-2 tabular-nums">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

EditableTable.displayName = "EditableTable";
